import { AlertTriangle, Clock } from 'lucide-react';
import { Etiqueta } from '@/components/ui/etiqueta';
import { formatoPrecio } from '@/lib/utils/formato-precio';
import { lecturaDelMercado } from '../lib/en-el-mercado';
import type { MercadoDelRecorrido } from '../lib/recorrido-del-precio';
import { EnlaceAProducto } from '@/components/ui/enlace-a-producto';

/**
 * El bloque «En el mercado» de la explicación (spec 225, pieza 2; lote B1,
 * maqueta `b1-fila`): las tiendas que cuentan, de la más barata a la más cara,
 * con hace cuánto se leyó cada una (en naranja si pasaron más de 30 días) y,
 * en gris, tu tienda, que no cuenta. Todo sale de `lib/en-el-mercado.ts`.
 *
 * `mercado.deHoy`: la propuesta es de antes y no guardó las tiendas; se
 * muestra el mercado de hoy y se dice.
 */
export function EnElMercado({
  mercado,
  queda,
  ahora,
  alProponer = false,
}: {
  mercado: MercadoDelRecorrido;
  /** El precio con que se compara («queda X»). null = sin precio que comparar. */
  queda: number | null;
  /** Para los tests. */
  ahora?: number;
  /**
   * Una propuesta resuelta: «hace cuánto» se mide contra cuándo se congeló
   * (`mercado.congeladoEn`), no contra hoy (auditoría del 3-oct).
   */
  alProponer?: boolean;
}) {
  const contraElCalculo = alProponer && mercado.congeladoEn != null;
  const l = lecturaDelMercado(
    mercado,
    queda,
    contraElCalculo ? Date.parse(mercado.congeladoEn!) : ahora,
    { alProponer: contraElCalculo }
  );
  return (
    <section
      aria-label="En el mercado"
      data-testid="en-el-mercado"
      className="overflow-hidden rounded-lg border text-sm"
    >
      <div className="flex flex-wrap items-baseline gap-x-2.5 border-b bg-muted/50 px-3 py-2">
        <span className="font-semibold">En el mercado</span>
        {l.cabecera && <span className="text-foreground/80">{l.cabecera}</span>}
        {mercado.deHoy && <span className="text-muted-foreground">· hoy</span>}
      </div>
      {mercado.deHoy && (
        <p
          className="border-b px-3 py-1.5 text-xs text-muted-foreground"
          data-testid="mercado-de-hoy"
        >
          Esta propuesta es de antes y no guardó las tiendas: es el mercado de hoy.
        </p>
      )}
      {l.tiendas.length === 0 && !l.propia ? (
        <p className="px-3 py-2 text-muted-foreground">
          Sin precios de la competencia que cuenten para este producto.
        </p>
      ) : (
        <table className="w-full">
          <tbody>
            {l.tiendas.map((t) => (
              <tr
                key={`${t.nombre}-${t.precio}`}
                data-testid="tienda"
                className="border-b last:border-b-0"
              >
                <td className="px-3 py-1.5">
                  <span
                    aria-hidden="true"
                    className="mr-2 inline-block h-2 w-2 rotate-45 bg-marca-mercado align-middle"
                  />
                  {t.nombre}
                  {t.laMasBarata && (
                    <Etiqueta tono="neutro" className="ml-1.5">
                      el más barato
                    </Etiqueta>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                  <EnlaceAProducto href={t.url} tienda={t.nombre}>
                    {formatoPrecio(t.precio)}
                  </EnlaceAProducto>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {t.vieja ? (
                    <span
                      className="inline-flex items-center gap-1 font-medium text-alerta"
                      data-testid="lectura-vieja"
                    >
                      <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                      {t.hace}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{t.hace}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">
                  {t.frente ?? ''}
                </td>
              </tr>
            ))}
            {l.propia && (
              <tr data-testid="tienda-propia" className="bg-muted/50 text-muted-foreground">
                <td className="px-3 py-1.5">
                  <span
                    aria-hidden="true"
                    className="mr-2 inline-block h-2 w-2 rotate-45 bg-border align-middle"
                  />
                  {l.propia.nombre} · tu tienda, no cuenta
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                  <EnlaceAProducto href={l.propia.url} tienda="tu tienda">
                    {formatoPrecio(l.propia.precio)}
                  </EnlaceAProducto>
                </td>
                <td />
                <td />
              </tr>
            )}
          </tbody>
        </table>
      )}
      {l.aviso && (
        <div className="flex items-start gap-1.5 border-t border-alerta-borde bg-alerta-suave px-3 py-2 text-xs text-alerta">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            <b className="font-semibold">{l.aviso}</b>{' '}
            <span className="text-foreground/80">El mercado puede haber cambiado.</span>
          </span>
        </div>
      )}
    </section>
  );
}
